/* Context */
import { useEffect, useContext, useRef } from 'react'
import ApiContext from 'src/ApiContext'
import { AppContext } from 'src/AppContext'

/* Types */
import { isApiResponse } from 'src/types/response'
import { isLoginResponse } from 'src/shared/apiBackendResponses/login'

const AutoLogin:React.FC = () => {
  const api = useContext(ApiContext)
  const { appState:{user}, appDispatch } = useContext(AppContext)

  const userRef = useRef(user)
  userRef.current = user

  useEffect(() => {
    let active = true
    let latest = 0
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    const refresh = async (retry = 0) => {
      clearTimeout(retryTimer)
      const currentUser = userRef.current
      if (!currentUser) return
      const revision = ++latest
      let resUser: unknown
      try { resUser = await api.authenticate(currentUser) }
      catch (_error) {
        // Preserve edits during an outage and retry one transient login failure.
        // Later transport reconnects always start a fresh authentication attempt.
        if (active && revision === latest && retry === 0 && api.socket.connected !== false)
          retryTimer = setTimeout(() => { void refresh(1) }, 1000)
        return
      }
      if (!active || revision !== latest) return
      if (
        !isApiResponse(resUser)
        || !isLoginResponse(resUser.response)
        || resUser.response === false
      ) {
        appDispatch({ type: 'LOGOUT' })
        return
      }
      if (resUser.response.rights.length !== currentUser.rights.length
        || !resUser.response.rights.every((r, i) => r === currentUser.rights[i]))
        appDispatch({ type: 'RIGHTS', payload: resUser.response.rights })
    }

    const onRights = (rights: unknown) => {
      if ( Array.isArray(rights) && rights.every((r): r is string => typeof r === 'string') ) {
        appDispatch({ type: 'RIGHTS', payload: rights })
        void refresh()
      }
    }
    const onConnect = () => { void refresh() }
    api.socket.on('rights', onRights)
    // A socket connect occurs after every new transport session. The manager's
    // reconnect event fires earlier and used to leave duplicate login listeners.
    api.socket.on('connect', onConnect)
    if (api.socket.connected !== false) void refresh()
    return () => {
      active = false
      latest += 1
      clearTimeout(retryTimer)
      api.socket.off('rights', onRights)
      api.socket.off('connect', onConnect)
      api.clearAuthentication()
    }
  }, [api, appDispatch])

  return null

}

export default AutoLogin
